import { logger } from "matrix-js-sdk/src/logger";

import { createAudioContext } from "./compat";
import { hasProperty } from "../dth/utils";

const formatMap = {
    mp3: "audio/mpeg",
    ogg: "audio/ogg",
};

export class BackgroundAudio {
    private audioContext = createAudioContext();
    private sounds: Record<string, AudioBuffer> = {};

    public async pickFormatAndPlay<F extends Array<keyof typeof formatMap>>(
        urlPrefix: string,
        formats: F,
        loop = false,
        abortController?: AbortController,
    ): Promise<AudioBufferSourceNode> {
        const format = this.pickFormat(...formats);
        if (!format) {
            console.log("Browser doesn't support any of the formats", formats);
            // Will probably never happen. If happened, format="" and will fail to load audio. Who cares...
        }

        return this.play(`${urlPrefix}.${format}`, loop, abortController);
    }

    public async play(url: string, loop = false, abortController?: AbortController): Promise<AudioBufferSourceNode> {
        if (!hasProperty(this.sounds, url)) {
            // No cache, fetch it
            let response: Response;
            try {
                response = await fetch(url, abortController ? { signal: abortController.signal } : undefined);
            } catch (err) {
                if (abortController && abortController.signal.aborted) {
                    throw new Error("Audio fetch aborted");
                }
                throw err;
            }
            if (response.status != 200) {
                logger.warn("Failed to fetch error audio");
            }
            let buffer: ArrayBuffer;
            try {
                buffer = await response.arrayBuffer();
            } catch (err) {
                if (abortController && abortController.signal.aborted) {
                    throw new Error("Audio fetch aborted");
                }
                throw err;
            }
            let sound: AudioBuffer;
            try {
                sound = await this.audioContext.decodeAudioData(buffer);
            } catch (err) {
                throw err;
            }
            this.sounds[url] = sound;
        }
        const source = this.audioContext.createBufferSource();
        source.buffer = this.sounds[url];
        source.loop = loop;
        source.connect(this.audioContext.destination);

        await this.audioContext.resume();
        source.onended = () => {
            this.audioContext.suspend();
        };

        source.start();
        return source;
    }

    private pickFormat<F extends Array<keyof typeof formatMap>>(...formats: F): F[number] | null {
        // Detect supported formats
        const audioElement = document.createElement("audio");

        for (const format of formats) {
            if (audioElement.canPlayType(formatMap[format])) {
                return format;
            }
        }
        return null;
    }
}
